import {
  Canvas,
  CreateFolderRequest,
  FlowSession,
  Folder,
  FOLDER_RESOURCE_TYPE_FLOW_SESSION,
  InboxItem,
  UpdateFolderRequest,
} from "@cohesive/api-client";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

/**
 * Canvas caches that embed a canvas and therefore its `folder_id`. A move must
 * patch + invalidate all of them so the canvas reappears under its new folder
 * everywhere (rail, recents, workspace grid). Mirrors the favorite-toggle set.
 */
const CANVAS_QUERY_KEYS: string[][] = [
  ["canvases"],
  ["canvas"],
  ["workspaces"],
  ["workspace"],
];

/** All folders of one resource type in a workspace (includes empty folders). */
export function useFolders(
  workspaceId: string | undefined,
  options?: Partial<UseQueryOptions<Folder[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["folders", workspaceId],
    queryFn: () => orgAPI.folders.list({ workspaceId: workspaceId! }),
    enabled: !!workspaceId,
    ...options,
  });
}

export function useCreateFolder() {
  const queryClient = useQueryClient();
  return useMutation<Folder, AxiosError, CreateFolderRequest>({
    mutationFn: (data) => orgAPI.folders.create(data),
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["folders", variables.workspace_id],
      });
    },
  });
}

export function useUpdateFolder(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    Folder,
    AxiosError,
    { folderId: string; data: UpdateFolderRequest }
  >({
    mutationFn: ({ folderId, data }) => orgAPI.folders.update(folderId, data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["folders", workspaceId] });
    },
  });
}

export function useDeleteFolder(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, { folderId: string }>({
    mutationFn: ({ folderId }) => orgAPI.folders.remove(folderId),
    onSettled: () => {
      // Delete re-parents canvases, so refresh both folders and canvas caches.
      queryClient.invalidateQueries({ queryKey: ["folders", workspaceId] });
      for (const queryKey of CANVAS_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

interface SetCanvasFolderVariables {
  canvasId: string;
  /** Target folder, or null to file the canvas at the workspace root. */
  folderId: string | null;
  /** Workspace whose folder list membership may change. */
  workspaceId?: string;
}

/**
 * Move a canvas into a folder (or to the workspace root). Optimistically patches
 * `folder_id` across every cached list that can hold the canvas, then resyncs on
 * settle. Folder membership is shared state, so this is a true move.
 */
export function useSetCanvasFolder() {
  const queryClient = useQueryClient();

  return useMutation<Canvas, AxiosError, SetCanvasFolderVariables>({
    mutationFn: ({ canvasId, folderId }) =>
      orgAPI.canvas.setFolder(canvasId, folderId),
    onMutate: ({ canvasId, folderId }) => {
      for (const queryKey of CANVAS_QUERY_KEYS) {
        queryClient.setQueriesData({ queryKey }, (data: unknown) => {
          if (!Array.isArray(data)) return data;
          return data.map((item) => {
            if (
              !item ||
              typeof item !== "object" ||
              (item as { id?: string }).id !== canvasId
            ) {
              return item;
            }
            return { ...item, folder_id: folderId ?? undefined };
          });
        });
      }
    },
    onSettled: (_data, _error, { workspaceId }) => {
      for (const queryKey of CANVAS_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey });
      }
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: ["folders", workspaceId] });
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Chat (flow session) folders — the same shared folders primitive, scoped to
// resource_type "flow-session". Chat folder lists are keyed under
// ["folders", workspaceId, "flow-session"], a prefix-extension of the canvas
// folder key so the create/rename/move mutations above (which invalidate
// ["folders", workspaceId]) refresh them too. Every chat list shares the
// ["workspace-flow-sessions"] prefix.
// ---------------------------------------------------------------------------

/** All chat folders in a workspace (includes empty folders). */
export function useChatFolders(
  workspaceId: string | undefined,
  options?: Partial<UseQueryOptions<Folder[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["folders", workspaceId, FOLDER_RESOURCE_TYPE_FLOW_SESSION],
    queryFn: () =>
      orgAPI.folders.list({
        workspaceId: workspaceId!,
        resourceType: FOLDER_RESOURCE_TYPE_FLOW_SESSION,
      }),
    enabled: !!workspaceId,
    ...options,
  });
}

/** Delete a chat folder; re-parents its chats up, so refresh chat lists too. */
export function useDeleteChatFolder(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, { folderId: string }>({
    mutationFn: ({ folderId }) => orgAPI.folders.remove(folderId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["folders", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["workspace-flow-sessions"] });
    },
  });
}

interface SetFlowSessionFolderVariables {
  flowSessionId: string;
  /** Target folder, or null to file the chat at the workspace root. */
  folderId: string | null;
  /** Workspace whose folder list membership may change. */
  workspaceId?: string;
}

/**
 * Move a chat into a folder (or to the workspace root). Optimistically patches
 * the nested `flow_session.folder_id` across every cached chat list, then
 * resyncs on settle. Folder membership is shared state, so this is a true move.
 */
export function useSetFlowSessionFolder() {
  const queryClient = useQueryClient();

  return useMutation<FlowSession, AxiosError, SetFlowSessionFolderVariables>({
    mutationFn: ({ flowSessionId, folderId }) =>
      orgAPI.flowSessions
        .setFolder(flowSessionId, folderId)
        .then((r) => r.data.flow_session),
    onMutate: ({ flowSessionId, folderId }) => {
      queryClient.setQueriesData(
        { queryKey: ["workspace-flow-sessions"] },
        (data: unknown) => {
          if (!Array.isArray(data)) return data;
          return data.map((item) => {
            if (
              !item ||
              typeof item !== "object" ||
              (item as InboxItem).flow_session_id !== flowSessionId
            ) {
              return item;
            }
            const inboxItem = item as InboxItem;
            if (!inboxItem.flow_session) return inboxItem;
            return {
              ...inboxItem,
              flow_session: {
                ...inboxItem.flow_session,
                folder_id: folderId ?? undefined,
              },
            };
          });
        },
      );
    },
    onSettled: (_data, _error, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ["workspace-flow-sessions"] });
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: ["folders", workspaceId] });
      }
    },
  });
}
